import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { leaveTypeApi, F908 } from '../../../services/leaveTypeApi';

export type Mode = 'browse' | 'add' | 'edit';

const defaultForm = {
  code: '',
  name: '',
  category: 1,
  calUnit: 1,
  minPerTime: 0,
  maxPerTime: 0,
  maxPerYear: 0,
  deductRate: 0,
  hrReview: 1,
  firstApprovalMax: 0,
  includeHolidays: false,
  excludeWorkHours: false,
};

export function useLeaveTypeForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<F908[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);

  // Form fields
  const [form, setForm] = useState(defaultForm);

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  const mapRecToForm = (r: F908) => ({
    code: r.f908_01,
    name: r.f908_11,
    category: r.f908_10,
    calUnit: r.f908_12,
    minPerTime: r.f908_13,
    maxPerTime: r.f908_14,
    maxPerYear: r.f908_15,
    deductRate: r.f908_16,
    hrReview: r.f908_17,
    firstApprovalMax: r.f908_21,
    includeHolidays: r.f908_22,
    excludeWorkHours: r.f908_23,
  });

  // Load all records
  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await leaveTypeApi.list(1, 1000);
      setRecords(res.data || []);
      return res.data || [];
    } catch {
      message.error('載入失敗');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Sync form fields from current record
  useEffect(() => {
    if (current && mode === 'browse') {
      setForm(mapRecToForm(current));
    }
  }, [current, mode]);

  // Initial load
  useEffect(() => {
    loadRecords().then(data => {
      if (data.length > 0) setCurrentIdx(0);
    });
  }, [loadRecords]);

  // Navigation
  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  // Start add mode
  const startAdd = () => {
    setMode('add');
    setForm({ ...defaultForm });
  };

  // Start edit mode
  const startEdit = () => {
    if (!current) return;
    setMode('edit');
    setForm(mapRecToForm(current));
  };

  // Cancel (restore)
  const cancel = () => {
    setMode('browse');
    if (current) {
      setForm(mapRecToForm(current));
    }
  };

  // Save
  const save = async () => {
    try {
      if (mode === 'add') {
        await leaveTypeApi.create({
          f908_01: form.code,
          f908_10: form.category,
          f908_11: form.name,
          f908_12: form.calUnit,
          f908_13: form.minPerTime,
          f908_14: form.maxPerTime,
          f908_15: form.maxPerYear,
          f908_16: form.deductRate,
          f908_17: form.hrReview,
          f908_21: form.firstApprovalMax,
          f908_22: form.includeHolidays,
          f908_23: form.excludeWorkHours,
        });
        message.success('新增成功');
      } else {
        await leaveTypeApi.update(current!.f908_01, {
          f908_10: form.category,
          f908_12: form.calUnit,
          f908_13: form.minPerTime,
          f908_14: form.maxPerTime,
          f908_15: form.maxPerYear,
          f908_16: form.deductRate,
          f908_17: form.hrReview,
          f908_21: form.firstApprovalMax,
          f908_22: form.includeHolidays,
          f908_23: form.excludeWorkHours,
        });
        message.success('修改成功');
      }
      const data = await loadRecords();
      const targetCode = mode === 'add' ? form.code : current!.f908_01;
      const idx = data.findIndex((r: F908) => r.f908_01 === targetCode);
      setCurrentIdx(idx >= 0 ? idx : 0);
      setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        vErrs.forEach((e: any) => message.error(e.message));
      } else {
        message.error(err?.response?.data?.error || '儲存失敗');
      }
    }
  };

  // Delete
  const doDelete = async () => {
    if (!current) return;
    try {
      await leaveTypeApi.delete(current.f908_01);
      message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) {
        setCurrentIdx(Math.min(currentIdx, data.length - 1));
      } else {
        setCurrentIdx(-1);
      }
    } catch {
      message.error('刪除失敗');
    }
  };

  // Quick search with 3-tier feedback
  const quickSearch = (searchText: string) => {
    if (isEditing) return;
    // Tier 1: Exact match
    const exact = records.findIndex(r => r.f908_01 === searchText);
    if (exact >= 0) {
      setCurrentIdx(exact);
      return;
    }
    // Tier 2: Near match (first record >= searchText)
    const near = records.findIndex(r => r.f908_01 >= searchText);
    if (near >= 0) {
      setCurrentIdx(near);
      message.info(`查無「${searchText}」，顯示最接近的記錄「${records[near].f908_01}」`);
      return;
    }
    // Tier 3: Not found
    message.warning(`查無此代號「${searchText}」`);
  };

  const setField = (field: string, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  return {
    mode, isEditing, loading,
    records, current, currentIdx,
    form, setField,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, quickSearch,
    loadRecords,
  };
}
